/**
 * Pure helpers over the live-alarm snapshot (AlarmsLive), kept out of the store
 * and the React tree so they're unit-testable and shareable. Both the audible
 * siren runner and any code that needs "is anything firing?" use these, so the
 * definition of an active alarm lives in one place.
 */
import type { AlarmsLive } from '@/store/alarms';

/**
 * The alarm keys in PRIORITY order. Anchor drag and a closing AIS CPA come first
 * because those are the ones most likely to put the boat or crew in danger right
 * now; the rest follow. Used to order what a single notification leads with when
 * several trip together, and to give each a plain label.
 */
export const ALARM_PRIORITY: { key: keyof AlarmsLive | 'aisCpa' | 'aisProximity'; label: string }[] =
  [
    { key: 'anchor', label: 'Anchor dragging' },
    { key: 'aisCpa', label: 'AIS collision risk' },
    { key: 'grounding', label: 'Grounding ahead' },
    // GNSS integrity sits high: if the fix has jumped or gone, the boat's place on
    // the chart can't be trusted, which undermines every other alarm below.
    { key: 'gnssJump', label: 'Possible GNSS spoofing' },
    { key: 'gnssLost', label: 'GNSS fix lost' },
    { key: 'depth', label: 'Shallow water' },
    { key: 'clearingDepth', label: 'Clearing depth crossed' },
    { key: 'clearing', label: 'Clearing bearing crossed' },
    { key: 'offTrack', label: 'Off track' },
    { key: 'proximity', label: 'Charted hazard near' },
    { key: 'tideHeight', label: 'Tide height limit' },
    { key: 'gnssAccuracy', label: 'GNSS accuracy poor' },
    { key: 'waypointArrival', label: 'Waypoint arrival' },
    { key: 'aisProximity', label: 'AIS target near' },
    { key: 'lowBattery', label: 'Low battery' },
  ];

/** The union of alarm keys used across the priority list and the per-alarm sound
 *  config, so both sides stay pinned to the same set. */
export type AlarmKey = (typeof ALARM_PRIORITY)[number]['key'];

/** True when a single live-alarm field is currently firing (non-null, non-empty). */
function fieldActive(live: AlarmsLive, key: (typeof ALARM_PRIORITY)[number]['key']): boolean {
  switch (key) {
    case 'aisCpa':
      return !!live.ais?.cpa;
    case 'aisProximity':
      return !!live.ais?.proximity;
    case 'clearing':
      return !!live.clearing && live.clearing.length > 0;
    case 'clearingDepth':
      return !!live.clearingDepth && live.clearingDepth.length > 0;
    default:
      return live[key as keyof AlarmsLive] != null;
  }
}

/** Whether any alarm at all is currently firing. */
export function anyAlarmActive(live: AlarmsLive): boolean {
  return ALARM_PRIORITY.some((a) => fieldActive(live, a.key));
}

/**
 * The labels of every currently-firing alarm, in priority order. Empty when
 * nothing is firing. Handy for a notification body ("Anchor dragging, Shallow
 * water") and for logging which alarms stood together.
 */
export function activeAlarmLabels(live: AlarmsLive): string[] {
  return ALARM_PRIORITY.filter((a) => fieldActive(live, a.key)).map((a) => a.label);
}

/** The single highest-priority active alarm's label, or null when silent. */
export function topAlarmLabel(live: AlarmsLive): string | null {
  const first = ALARM_PRIORITY.find((a) => fieldActive(live, a.key));
  return first ? first.label : null;
}

/** The single highest-priority active alarm's KEY, or null when silent. Used to
 *  pick which alarm's chosen tone and volume the siren should play. */
export function topAlarmKey(live: AlarmsLive): AlarmKey | null {
  const first = ALARM_PRIORITY.find((a) => fieldActive(live, a.key));
  return first ? first.key : null;
}

/**
 * The alarm key the audible siren should sound RIGHT NOW, honouring master mute.
 * It's the one gate every audible/notification path runs through: when muted it
 * returns null so nothing sounds, even though the underlying alarm is still
 * firing (topAlarmKey stays non-null, so the banner and detection are untouched).
 * Pure so the mute behaviour is unit-testable without mounting the runner.
 */
export function audibleAlarmKey(
  live: AlarmsLive,
  masterMuted: boolean,
  acked: Record<string, unknown> = {},
): AlarmKey | null {
  if (masterMuted) return null;
  // Skip acknowledged alarms: the crew has answered this episode, and a siren
  // that cannot be answered teaches the crew to mute everything instead.
  const first = ALARM_PRIORITY.find((a) => fieldActive(live, a.key) && !acked[a.key]);
  return first ? first.key : null;
}
